import { InstallationRefusal } from '@core/helpers/installation-refusal.helper';

export interface PlacedCoordinateRefusal {
  messageKey: string;
  // What the dialog was opened on is gone or no longer the caller's, so the page must read it again.
  stale: boolean;
}

const KEY_PREFIX = 'locations.coordinates.errors.';
const GEOCODER_UNAVAILABLE = 'geocoder_unavailable';

// The reasons a pair is refused with, whether a preview reports them or a save fails with them.
const RESOLUTION_REFUSALS: Record<string, string> = {
  coordinates_resolve_to_no_zone: 'resolveToNoZone',
  coordinates_outside_location_country: 'outsideCountry',
  location_country_unrecognised: 'countryUnrecognised',
  location_hosts_installations_needs_coordinates: 'hostsInstallations',
  shared_device_timezone_conflict: 'sharedDeviceTimezone',
  location_address_geocodes: 'addressGeocodes',
  location_has_no_address: 'noAddress',
  // The preview reports it as its refusal; the save answers it with a 503.
  geocoder_unavailable: 'geocoderUnavailable',
};

// A reason can carry a detail after its code ("coordinates_outside_location_country: Europe/Berlin
// does not serve NL"), so it is the text before the first colon that names the refusal.
function reasonCode(reason: string): string {
  return reason.split(':', 1)[0].trim();
}

// Whether a failed request carries one of those reasons, which a failed preview explains in place.
export function isResolutionRefusal({ status, reason }: InstallationRefusal): boolean {
  return status === 409 && reason !== null && reasonCode(reason) in RESOLUTION_REFUSALS;
}

export function resolutionRefusalKey(reason: string): string {
  return KEY_PREFIX + (RESOLUTION_REFUSALS[reasonCode(reason)] ?? 'notResolved');
}

// A 400 or a 422 means the request itself was wrong, which the user cannot fix, so it reads like
// any other failure to save.
export function placeCoordinateRefusal(refusal: InstallationRefusal): PlacedCoordinateRefusal {
  const { status, reason } = refusal;
  if (status === 409 && reason !== null && isResolutionRefusal(refusal)) {
    return { messageKey: resolutionRefusalKey(reason), stale: false };
  }
  if (status === 503 && reason !== null && reasonCode(reason) === GEOCODER_UNAVAILABLE) {
    return { messageKey: resolutionRefusalKey(reason), stale: false };
  }
  if (status === 401 || status === 403) {
    return { messageKey: KEY_PREFIX + 'notAllowed', stale: true };
  }
  if (status === 404) {
    return { messageKey: KEY_PREFIX + 'gone', stale: true };
  }
  return { messageKey: KEY_PREFIX + 'notSaved', stale: false };
}
